import { Observable, from } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { fetchAuthSession } from 'aws-amplify/auth'
import { createAuthLink } from 'aws-appsync-auth-link'
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link'

const isTokenExpiredError = ({ graphQLErrors, networkError }: { graphQLErrors?: any; networkError?: any }) => {
  if (
    graphQLErrors?.some(
      e =>
        e.errorType === 'UnauthorizedException' ||
        e.extensions?.errorType === 'UnauthorizedException' ||
        e.message?.includes('Token has expired'),
    )
  )
    return true
  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) return true
  return false
}

export const createLink = auth => {
  const url = `https://appsync-${process.env.NEXT_PUBLIC_STAGE}.pubgen.ai/graphql`
  const region = process.env.NEXT_PUBLIC_AWS_REGION

  // When a user's Cognito ID token expires and the automatic refresh fails (e.g. due to a
  // momentary network drop), AppSync returns 401 "Token has expired". This link catches that,
  // force-refreshes the token, and retries the request once — avoiding a full-page error.
  const tokenRefreshLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (!isTokenExpiredError({ graphQLErrors, networkError })) return
    if (operation.getContext().hasRetried) return

    operation.setContext({ hasRetried: true })

    return new Observable(observer => {
      fetchAuthSession({ forceRefresh: true })
        .then(() => {
          forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          })
        })
        .catch(err => {
          observer.error(err)
        })
    })
  })

  return from([
    tokenRefreshLink,
    createAuthLink({ url, region, auth }),
    createSubscriptionHandshakeLink({ url, region, auth }),
  ])
}
